import { useCallback, useEffect, useMemo, useState } from "react"
import { relevantRungs } from "../../../engine/precall/troubleshootingLadders"

/**
 * Tient la position dans une échelle de dépannage : quel essai est proposé, et
 * ce qui a déjà été tenté.
 *
 * L'échelle est recalculée à chaque changement de situation — c'est ce qui fait
 * que les barreaux sans objet disparaissent d'eux-mêmes. Elle se remet à zéro
 * dès que l'appareil se met à fonctionner : si un élève résout puis recasse son
 * micro, il ne reprend pas au barreau 4.
 */
export const useTroubleshooting = ({ ladder, cause, isWeb, deviceCount, isVideoMuted, isResolved }) => {
    const rungs = useMemo(
        () => relevantRungs(ladder, { cause, isWeb, deviceCount, isVideoMuted }),
        [ladder, cause, isWeb, deviceCount, isVideoMuted]
    )

    const [index, setIndex] = useState(0)
    const [attempts, setAttempts] = useState([])

    useEffect(() => {
        if (isResolved) {
            setIndex(0)
            setAttempts([])
        }
    }, [isResolved])

    // La situation a changé (un refus devient un silence, par exemple) : la
    // liste des barreaux n'est plus la même, on repart de son début.
    useEffect(() => {
        setIndex(0)
    }, [cause])

    const current = index < rungs.length ? rungs[index] : null

    /** Appelée quand la re-vérification a échoué : on note l'essai et on descend. */
    const advance = useCallback(() => {
        const tried = index < rungs.length ? rungs[index].label : null
        if (tried) setAttempts((previous) => (previous.includes(tried) ? previous : [...previous, tried]))
        setIndex((previous) => previous + 1)
    }, [index, rungs])

    return {
        current,
        // La cause est renvoyée avec le barreau parce que le conseil en dépend :
        // « le système bloque ton micro » et « le navigateur bloque ton micro »
        // sont deux textes du même barreau, et appellent deux gestes différents.
        // Les tenir ensemble ici évite d'avoir à les réassocier à l'affichage —
        // c'est en les réassociant à la main qu'on avait perdu la variante système.
        cause,
        attempts,
        position: Math.min(index + 1, rungs.length),
        total: rungs.length,
        advance,
    }
}
